import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import {
  AssetHeaderComponent,
  AssetHeaderConfig,
} from '../../../../shared/components/asset-header/asset-header.component';
import { DiscussionSidebarComponent } from '../../discussion-sidebar/discussion-sidebar.component';
import {
  DiscussionListComponent,
  DiscussionItem,
} from '../../shared/discussion-list/discussion-list.component';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../../shared/components/login-modal/login-modal.component';
import { Router } from '@angular/router';
import { ToasterService } from '../../../../services/toaster.service';
import { DiscussionPaginationComponent } from '../../shared/discussion-pagination/discussion-pagination.component';
import { parseDiscussionPaginationMeta } from '../../shared/discussion-pagination.util';

@Component({
  selector: 'app-discussion-my-pinned',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    DiscussionSidebarComponent,
    DiscussionListComponent,
    DiscussionPaginationComponent,
    LoginModalComponent,
  ],
  templateUrl: './my-pinned.component.html',
  styleUrl: './my-pinned.component.scss',
})
export class DiscussionMyPinnedComponent implements OnInit {
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakService = inject(KeycloakService);
  private toasterService = inject(ToasterService);

  discussions: DiscussionItem[] = [];
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;
  activeTab: string = 'my-pinned';

  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  readonly itemsPerPage = 10;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Discussions', route: '/discussions' },
      { label: 'My Pinned' },
    ],
    title: 'My Pinned',
    description: 'View your pinned discussions',
  };

  ngOnInit(): void {
    this.checkLoginStatus();
    this.fetchPinnedDiscussions();
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloakService.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async fetchPinnedDiscussions(): Promise<void> {
    try {
      const response = await this.discussionApiService.getPinnedDiscussions({
        filters: {
          type: null,
          status: null,
          tags: null,
        },
        sort_by: 'newest',
        page: this.currentPage,
        limit: this.itemsPerPage,
        pinned: true,
      });

      const list = this.getDiscussionsListFromResponse(response);
      this.discussions = list.map((item: any) =>
        this.transformToDiscussionItem(item),
      );

      const pagination = parseDiscussionPaginationMeta(response);
      this.totalCount = pagination.totalCount || this.discussions.length;
      this.totalPages = pagination.totalPages;
    } catch (error) {
      console.error('Error fetching pinned discussions:', error);
      this.discussions = [];
      this.totalPages = 1;
      this.totalCount = 0;
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.fetchPinnedDiscussions();
  }

  /** Extract discussions array from API response (handles pinned_discussions, discussions, etc.) */
  private getDiscussionsListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.pinned_discussions && Array.isArray(data.pinned_discussions))
      return data.pinned_discussions;
    if (data?.discussions && Array.isArray(data.discussions))
      return data.discussions;
    if (data?.discussion && Array.isArray(data.discussion))
      return data.discussion;
    return [];
  }

  transformToDiscussionItem(item: any): DiscussionItem {
    return {
      id: item.id || item._id || '',
      title: item.title || 'Untitled Discussion',
      description: item.description || item.content || '',
      author:
        item.user?.name ||
        item.author?.name ||
        item.author ||
        item.created_by ||
        'Unknown',
      category: item.category ?? '',
      discussionType:
        [item.type, item.discussion_type]
          .map((v) => (v != null && String(v).trim() ? String(v).trim() : ''))
          .find((s) => s) || undefined,
      replies: item.reply_count ?? item.replies ?? item.comment_count ?? 0,
      views: item.view_count ?? item.views ?? 0,
      lastActivity:
        item.updated_at ??
        item.updatedAt ??
        item.last_activity ??
        item.created_at ??
        item.createdAt ??
        new Date().toISOString(),
      createdAt: item.created_at ?? item.createdAt ?? undefined,
      isPinned: item.is_pinned ?? item.pinned ?? false,
      isBookmarked: item.is_bookmarked ?? item.bookmarked ?? false,
      tags: item.tags || [],
      attachments: item.attachments || [],
    };
  }

  createNewDiscussion(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    this.router.navigate(['/discussions/create-new']);
  }

  async onBookmarkClick(event: {
    event: Event;
    discussion: DiscussionItem;
  }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newBookmarkState = !event.discussion.isBookmarked;
    const previousState = event.discussion.isBookmarked;
    event.discussion.isBookmarked = newBookmarkState;
    try {
      if (previousState) {
        await this.discussionApiService.unbookmarkDiscussion(
          event.discussion.id,
        );
      } else {
        await this.discussionApiService.bookmarkDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newBookmarkState
          ? 'This discussion has been added to your bookmarks. You can open it anytime from My Bookmarks.'
          : 'This discussion has been removed from your bookmarks.',
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      event.discussion.isBookmarked = previousState;
      this.toasterService.error(
        (error as any)?.error?.detail || 'Failed to update bookmark',
      );
    }
  }

  async onPinClick(event: {
    event: Event;
    discussion: DiscussionItem;
  }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newPinState = !event.discussion.isPinned;
    const previousState = event.discussion.isPinned;
    event.discussion.isPinned = newPinState;
    try {
      if (previousState) {
        await this.discussionApiService.unpinDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.pinDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newPinState
          ? 'This discussion is now pinned. Find it under My Pinned in Discussions.'
          : 'This discussion has been unpinned.',
      );
      if (!newPinState) {
        this.discussions = this.discussions.filter(
          (d) => d.id !== event.discussion.id,
        );
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
      event.discussion.isPinned = previousState;
      this.toasterService.error(
        (error as any)?.error?.detail || 'Failed to update pin',
      );
    }
  }
}
